import { clearAuth, getAccessToken, setAccessToken } from '@/lib/auth'

const API_BASE_URL = import.meta.env.VITE_API_URL

interface RequestConfig extends RequestInit {
  skipAuth?: boolean
}

async function request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((config.headers as Record<string, string>) || {}),
  }

  if (!config.skipAuth) {
    const token = getAccessToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  const response = await fetch(url, {
    ...config,
    headers,
    credentials: 'include',
  })

  // Handle 401 - try to refresh token (skip for auth endpoints)
  if (response.status === 401 && !config.skipAuth) {
    const refreshed = await tryRefreshToken()
    if (refreshed) {
      const newToken = getAccessToken()
      if (newToken) {
        headers.Authorization = `Bearer ${newToken}`
      }
      const retryResponse = await fetch(url, {
        ...config,
        headers,
        credentials: 'include',
      })
      if (!retryResponse.ok) {
        throw new Error(await parseError(retryResponse))
      }
      return parseResponse(retryResponse)
    }
    // Refresh failed - clear auth and throw (don't redirect here)
    clearAuth()
    throw new Error('Session expired')
  }

  if (!response.ok) {
    throw new Error(await parseError(response))
  }

  return parseResponse(response)
}

async function tryRefreshToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) return false

    const data = await response.json()
    if (data.access_token) {
      setAccessToken(data.access_token)
      return true
    }
    return false
  } catch {
    return false
  }
}

async function parseError(response: Response): Promise<string> {
  try {
    const data = await response.json()
    return data.message || data.detail || `HTTP ${response.status}`
  } catch {
    return `HTTP ${response.status}`
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T
  }
  return response.json()
}

export const client = {
  get: <T>(endpoint: string, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'GET' }),

  post: <T>(endpoint: string, body: unknown, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'POST', body: JSON.stringify(body) }),

  patch: <T>(endpoint: string, body: unknown, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'PATCH', body: JSON.stringify(body) }),

  delete: <T>(endpoint: string, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'DELETE' }),
}
